import axios from "axios";
import React, { ChangeEvent, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { uuidv7 as uuid } from "uuidv7";

import CustomRadio from "src/app/components/CustomRadio/CustomRadio";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import Modal, {
  ModalRow,
  ModalTitle,
  OutsideClickModes,
} from "src/app/components/Modal/Modal";
import { MetaFileData } from "src/app/metafields/MultipleFileField/MultipleFileField";
import { translateMessage } from "src/app/methods/translateMessage";
import { RootState } from "src/redux/reducers";
import { PaymentsService } from "src/services";
import { ExtensionTypeEnum, WsAssetParentTypeEnum } from "src/types";
import { parseErrorMessages, showErrorToast } from "src/utils/methods";
import { showToast } from "src/app/methods/showToast";
import PaymentConfig from "./components/PaymentConfig";
import UploadFileField from "./components/UploadFileField";
import useGetExtensionDetails from "./data/useGetExtensionDetails";
import { PaymentType } from "./types";
import { isSingleAndSplitPaymentDetails } from "./utils";

import "./CreatePaymentModal.scss";

interface CreatePaymentModalProps {
  onClose: () => void;
}

export default function CreatePaymentModal({
  onClose,
}: CreatePaymentModalProps) {
  const [paymentType, setPaymentType] = useState<PaymentType>(
    PaymentType.SinglePayment,
  );
  const [selectedPercentage, setSelectedPercentage] = useState<number | null>(
    null,
  );
  const [firstPaymentDate, setFirstPaymentDate] = useState<string>("");
  const [secondPaymentDate, setSecondPaymentDate] = useState<string>("");
  const [isSendingPayment, setIsSendingPayment] = useState(false);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);

  const [firstPaymentFile, setFirstPaymentFile] = useState<MetaFileData[]>([]);
  const [secondPaymentFile, setSecondPaymentFile] = useState<MetaFileData[]>(
    [],
  );
  const [paymentUuid] = useState(uuid());

  const [extensionDetailsState, initializeExtensionDetailsFetch] =
    useGetExtensionDetails();

  const params = useParams<{
    projectId: string;
  }>();

  const { projectId } = params;

  const {
    mainReducer: { activeWorkspaceUuid },
    taskReducer: { selectedTasks },
    projectReducer: { taskType, projectBasicData },
  } = useSelector((state: RootState) => state);

  const intl = useIntl();

  const wsMetaFieldUuid =
    extensionDetailsState.type === "done" &&
    isSingleAndSplitPaymentDetails(extensionDetailsState.data)
      ? extensionDetailsState.data.paymentFileLocalTaskMetaFieldUuid
      : "";
  const paymentFileWsTaskMetaFieldName =
    extensionDetailsState.type === "done" &&
    isSingleAndSplitPaymentDetails(extensionDetailsState.data)
      ? extensionDetailsState.data.paymentFileLocalTaskMetaFieldName
      : null;

  const handleConfirm = async () => {
    setIsSendingPayment(true);

    if (!selectedPercentage) {
      console.error("Missing payment percentage");
      return;
    }

    const firstPayment = selectedPercentage.toString();
    const secondPayment = Number(
      (1 - selectedPercentage).toFixed(1),
    ).toString();

    const firstPaymentFileId = firstPaymentFile.map((file) => file.assetId);
    const secondPaymentFileId = secondPaymentFile.map((file) => file.assetId);

    try {
      await PaymentsService.create({
        wsTasksUuids: selectedTasks.map((task) => task.taskId),
        wsWorkspaceUuid: activeWorkspaceUuid,
        wsProjectUuid: projectId,
        wsTaskType: taskType,
        paymentData: {
          type: paymentType,
          amount:
            paymentType === PaymentType.SinglePayment
              ? [firstPayment]
              : [firstPayment, secondPayment],
          dueDate:
            paymentType === PaymentType.SinglePayment
              ? [firstPaymentDate]
              : [firstPaymentDate, secondPaymentDate],
          files: [...firstPaymentFile, ...secondPaymentFile].map((file) => ({
            name: file.name,
            mimeType: file.mimeType,
            realName: file.realName,
            size: file.size,
            wsAssetType: WsAssetParentTypeEnum.TYPE_WS_TASK_META_FIELD,
            wsMetaFieldUuid,
            wsObjectUuid: paymentUuid,
          })),
          wsPaymentTasksUuids:
            paymentType === PaymentType.SinglePayment
              ? firstPaymentFileId
              : [...firstPaymentFileId, ...secondPaymentFileId],
        },
      });
      onClose();
      showToast(
        "success",
        <IDHFormattedMessage id="ws_success" defaultMessage="Success" />,
        <IDHFormattedMessage
          id="ws_payment_success"
          defaultMessage="Payment has been created successfully."
        />,
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data?.errors) {
        const errorMessage = parseErrorMessages(error.response.data.errors)[0]
          .message;
        showErrorToast({
          id: errorMessage,
          defaultMessage: errorMessage,
        });
      } else {
        showErrorToast({
          id: "ws_payment_error",
          defaultMessage:
            "Something went wrong. Please try sending the payment again.",
        });
      }
    } finally {
      setIsSendingPayment(false);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPaymentType(event.target.value as PaymentType);
  };

  const shouldDisableConfirmButton =
    selectedPercentage === null ||
    (paymentType === PaymentType.SinglePayment &&
      firstPaymentDate.length === 0) ||
    (paymentType === PaymentType.SplitPayment &&
      (firstPaymentDate.length === 0 || secondPaymentDate.length === 0));

  useEffect(() => {
    if (!projectBasicData) return;

    const paymentExtension = projectBasicData.wsExtensions.find(
      (extension) =>
        extension.type === ExtensionTypeEnum.CreateSingleAndSplitPayments,
    );

    if (!paymentExtension) return;

    initializeExtensionDetailsFetch(paymentExtension.uuid);
  }, [projectBasicData, initializeExtensionDetailsFetch]);

  return (
    <Modal
      isLoading={isSendingPayment}
      className="create-payment-modal"
      closeButtonText={
        <IDHFormattedMessage id="ws_cancel" defaultMessage="Cancel" />
      }
      confirmButtonDisabled={
        shouldDisableConfirmButton || isUploadingAttachment
      }
      onClose={onClose}
      displayCancelButton
      onConfirmClick={handleConfirm}
      confirmButtonText={
        <IDHFormattedMessage id="ws_create" defaultMessage="Create" />
      }
      outsideClick={OutsideClickModes.Warn}
    >
      <ModalTitle>
        <IDHFormattedMessage
          id="ws_create_payment"
          defaultMessage="Create payment"
        />
      </ModalTitle>
      <div className="create-payment-modal__wrapper">
        <ModalRow>
          <CustomRadio
            id="single-payment"
            name="payment"
            value="singlePayment"
            checked={paymentType === PaymentType.SinglePayment}
            onChange={handleChange}
            label={translateMessage({
              intl,
              id: "ws_single_payment",
              defaultMessage: "Single payment",
            })}
          />

          <CustomRadio
            id="split-payment"
            name="payment"
            value="splitPayment"
            checked={paymentType === PaymentType.SplitPayment}
            onChange={handleChange}
            disabled={selectedPercentage === 1}
            label={translateMessage({
              intl,
              id: "ws_split_payment",
              defaultMessage: "Split payment",
            })}
          />
        </ModalRow>

        <ModalRow>
          <PaymentConfig
            paymentType={paymentType}
            selectedPercentage={selectedPercentage}
            setSelectedPercentage={setSelectedPercentage}
            setPaymentType={setPaymentType}
            paymentDate={firstPaymentDate}
            setPaymentDate={setFirstPaymentDate}
            uploadFileField={
              <UploadFileField
                selectedFiles={firstPaymentFile}
                setSelectedFiles={setFirstPaymentFile}
                isUploadingAttachment={isUploadingAttachment}
                setIsUploadingAttachment={setIsUploadingAttachment}
                wsMetaFieldUuid={wsMetaFieldUuid}
                paymentUuid={paymentUuid}
              />
            }
            paymentFileWsTaskMetaFieldName={paymentFileWsTaskMetaFieldName}
          />

          {paymentType === PaymentType.SplitPayment && selectedPercentage && (
            <PaymentConfig
              isSecondPayment
              paymentType={paymentType}
              selectedPercentage={selectedPercentage}
              setSelectedPercentage={setSelectedPercentage}
              setPaymentType={setPaymentType}
              paymentDate={secondPaymentDate}
              setPaymentDate={setSecondPaymentDate}
              uploadFileField={
                <UploadFileField
                  selectedFiles={secondPaymentFile}
                  setSelectedFiles={setSecondPaymentFile}
                  isUploadingAttachment={isUploadingAttachment}
                  setIsUploadingAttachment={setIsUploadingAttachment}
                  wsMetaFieldUuid={wsMetaFieldUuid}
                  paymentUuid={paymentUuid}
                />
              }
              paymentFileWsTaskMetaFieldName={paymentFileWsTaskMetaFieldName}
            />
          )}
        </ModalRow>
      </div>
    </Modal>
  );
}
